import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Terminal, Gauge } from "lucide-react";
import { useLanguage } from "@/lib/i18n";

const clsMap = {
  cmd: "text-neutral-200",
  info: "text-[#FFB800]",
  ok: "text-[#FF5A00]",
};

function TerminalWidget({ script }) {
  const [lines, setLines] = useState([]);
  const [current, setCurrent] = useState("");
  const state = useRef({ line: 0, char: 0 });

  useEffect(() => {
    const id = setInterval(() => {
      const s = state.current;
      const entry = script.lines[s.line];
      if (s.char < entry.t.length) {
        s.char += 1;
        setCurrent(entry.t.slice(0, s.char));
      } else {
        setLines((l) => [...l.slice(-6), entry]);
        setCurrent("");
        s.line = (s.line + 1) % script.lines.length;
        s.char = 0;
        if (s.line === 0) setLines([]);
      }
    }, 34);
    return () => clearInterval(id);
  }, [script]);

  return (
    <div
      className="border border-white/10 bg-black/50 backdrop-blur-md p-6 md:p-8 min-h-[320px] flex flex-col"
      data-testid="terminal-widget"
    >
      <div className="flex items-center gap-2 pb-5 border-b border-white/10">
        <span className="w-2.5 h-2.5 rounded-full bg-[#FF5A00]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#FFB800]" />
        <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
        <span className="ml-3 text-xs tracking-widest uppercase text-neutral-500 flex items-center gap-2">
          <Terminal className="w-3.5 h-3.5" /> {script.title}
        </span>
      </div>
      <div className="pt-5 font-mono text-xs md:text-sm leading-7 flex-1">
        {lines.map((l, i) => (
          <p key={i} className={clsMap[l.c]}>{l.t}</p>
        ))}
        <p className={clsMap[script.lines[state.current.line].c]}>
          {current}
          <span className="inline-block w-2 h-4 bg-[#FF5A00] align-middle ml-0.5 animate-pulse" />
        </p>
      </div>
    </div>
  );
}

function RoiWidget() {
  const [hours, setHours] = useState(12);
  const [team, setTeam] = useState(6);
  const { t } = useLanguage();
  const yearlyHours = hours * team * 52;
  const savings = yearlyHours * 45;

  return (
    <div
      className="border border-white/10 bg-black/50 backdrop-blur-md p-6 md:p-8 min-h-[320px] flex flex-col"
      data-testid="roi-widget"
    >
      <div className="flex items-center gap-2 pb-5 border-b border-white/10">
        <Gauge className="w-4 h-4 text-[#FFB800]" />
        <span className="text-xs tracking-widest uppercase text-neutral-500">
          {t("playground.roiTitle")}
        </span>
      </div>

      <div className="pt-8 flex-1 flex flex-col gap-10">
        <div>
          <div className="flex justify-between text-sm mb-3">
            <label htmlFor="roi-hours" className="text-neutral-400">{t("playground.roiHours")}</label>
            <span className="font-mono text-[#FFB800] tabular-nums" data-testid="roi-hours-value">{hours}h</span>
          </div>
          <input
            id="roi-hours"
            type="range"
            min="1"
            max="40"
            value={hours}
            onChange={(e) => setHours(+e.target.value)}
            className="w-full accent-[#FF5A00] cursor-pointer"
            data-testid="roi-hours-slider"
          />
        </div>
        <div>
          <div className="flex justify-between text-sm mb-3">
            <label htmlFor="roi-team" className="text-neutral-400">{t("playground.roiTeam")}</label>
            <span className="font-mono text-[#FFB800] tabular-nums" data-testid="roi-team-value">{team}</span>
          </div>
          <input
            id="roi-team"
            type="range"
            min="1"
            max="50"
            value={team}
            onChange={(e) => setTeam(+e.target.value)}
            className="w-full accent-[#FF5A00] cursor-pointer"
            data-testid="roi-team-slider"
          />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-6 pt-6 border-t border-white/10 mt-auto">
          <div>
            <p className="font-syne font-bold text-xl sm:text-2xl md:text-4xl text-[#FF5A00] tabular-nums" data-testid="roi-output-hours">
              {yearlyHours.toLocaleString()}h
            </p>
            <p className="text-xs text-neutral-500 mt-2">{t("playground.roiOut1")}</p>
          </div>
          <div>
            <p className="font-syne font-bold text-xl sm:text-2xl md:text-4xl text-[#FFB800] tabular-nums" data-testid="roi-output-savings">
              &euro;{savings.toLocaleString()}
            </p>
            <p className="text-xs text-neutral-500 mt-2">{t("playground.roiOut2")}</p>
          </div>
        </div>
        <p className="text-[10px] text-neutral-600 font-mono">{t("playground.roiNote")}</p>
      </div>
    </div>
  );
}

export default function Playground() {
  const { t, lang } = useLanguage();
  const script = { title: t("playground.terminalTitle"), lines: t("playground.terminal") };

  return (
    <section id="playground" className="py-32 border-t border-white/10" data-testid="playground-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 md:mb-20"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#FF5A00] mb-4" data-testid="playground-eyebrow">
            {t("playground.eyebrow")}
          </p>
          <h2 className="font-syne font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl" data-testid="playground-heading">
            {t("playground.heading")}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <TerminalWidget key={lang} script={script} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          >
            <RoiWidget />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
